// Fix EU (2026-10-01): la plataforma como app instalable (PWA) y ajustes para el celular.
// - registrarPWA(): registra el service worker (solo en la versión publicada).
// - usePWA(): si se puede instalar y la acción de instalar.
// - useAvisoVersionNueva(): avisa cuando se publicó una versión nueva.
// - useEnLinea(): si hay conexión.
// - En iPhone/iPad con la app instalada, los PDF y archivos se abren en un visor propio
//   con "Compartir / Guardar" (ahí iOS no deja abrir pestañas nuevas con la sesión).
import { useEffect, useState } from "react";
import { toast } from "sonner";

const PRODUCCION = process.env.NODE_ENV === "production";

export const esIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

export const esInstalada = () =>
  window.navigator.standalone === true || !!window.matchMedia?.("(display-mode: standalone)").matches;

// ── Instalar ────────────────────────────────────────────────────────────────
let eventoInstalar = null;
const oyentes = new Set();
const avisar = () => oyentes.forEach((f) => f());

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  eventoInstalar = e;
  avisar();
});
window.addEventListener("appinstalled", () => {
  eventoInstalar = null;
  avisar();
});

export function usePWA() {
  const [, setVuelta] = useState(0);
  useEffect(() => {
    const f = () => setVuelta((n) => n + 1);
    oyentes.add(f);
    return () => oyentes.delete(f);
  }, []);
  const instalada = esInstalada();
  return {
    instalada,
    puedeInstalar: !instalada && !!eventoInstalar,
    // En iPhone no hay botón de instalar: se agrega desde Compartir → "Agregar a inicio".
    iosSinInstalar: !instalada && !eventoInstalar && esIOS(),
    instalar: async () => {
      if (!eventoInstalar) return false;
      const ev = eventoInstalar;
      ev.prompt();
      const r = await ev.userChoice.catch(() => null);
      eventoInstalar = null;
      avisar();
      return r?.outcome === "accepted";
    },
  };
}

// ── Conexión ────────────────────────────────────────────────────────────────
export function useEnLinea() {
  const [enLinea, setEnLinea] = useState(navigator.onLine !== false);
  useEffect(() => {
    const si = () => setEnLinea(true);
    const no = () => setEnLinea(false);
    window.addEventListener("online", si);
    window.addEventListener("offline", no);
    return () => {
      window.removeEventListener("online", si);
      window.removeEventListener("offline", no);
    };
  }, []);
  return enLinea;
}

// ── Versión nueva publicada ─────────────────────────────────────────────────
const RE_PRINCIPAL = /\/static\/js\/main\.[0-9a-f]+\.js/;

export function useAvisoVersionNueva() {
  useEffect(() => {
    if (!PRODUCCION) return undefined;
    const actual = (document.querySelector('script[src*="/static/js/main."]')?.getAttribute("src") || "").match(RE_PRINCIPAL)?.[0];
    if (!actual) return undefined;
    let avisado = false;
    const revisar = async () => {
      if (avisado || document.visibilityState !== "visible" || navigator.onLine === false) return;
      try {
        const html = await (await fetch("/", { cache: "no-store", credentials: "same-origin" })).text();
        const publicada = html.match(RE_PRINCIPAL)?.[0];
        if (publicada && publicada !== actual) {
          avisado = true;
          toast("Hay una versión nueva de la plataforma", {
            description: "Guardá lo que estés cargando y tocá Actualizar.",
            duration: Infinity,
            action: { label: "Actualizar", onClick: () => window.location.reload() },
          });
        }
      } catch (_e) {
        /* sin conexión: se revisa en la próxima vuelta */
      }
    };
    const id = setInterval(revisar, 5 * 60 * 1000);
    document.addEventListener("visibilitychange", revisar);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", revisar);
    };
  }, []);
}

// ── iPhone/iPad con la app instalada: visor de archivos ─────────────────────
const mismoOrigen = (u) =>
  u.startsWith("blob:") || u.startsWith("data:") || u.startsWith("/") || u.startsWith(window.location.origin + "/");

async function mostrarArchivo(url, nombre = "") {
  const capa = document.createElement("div");
  capa.setAttribute("data-testid", "visor-archivo");
  capa.style.cssText =
    "position:fixed;inset:0;z-index:2147483000;background:#fff;display:flex;flex-direction:column;" +
    "padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);" +
    "font-family:Inter,-apple-system,system-ui,sans-serif";
  const boton = "border:1px solid #d1d5db;border-radius:10px;padding:8px 12px;font-size:14px;font-weight:600;background:#fff;color:#111827";
  capa.innerHTML =
    `<div style="display:flex;align-items:center;gap:8px;padding:10px 12px;border-bottom:1px solid #e5e7eb">` +
    `<button type="button" data-a="cerrar" style="${boton}">Cerrar</button>` +
    `<span data-a="nombre" style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px;color:#4b5563">Abriendo…</span>` +
    `<button type="button" data-a="compartir" disabled style="${boton};background:#2E6DA4;border-color:#2E6DA4;color:#fff;opacity:.5">Compartir / Guardar</button>` +
    `</div><div data-a="cuerpo" style="flex:1;overflow:auto;-webkit-overflow-scrolling:touch;display:flex"></div>`;
  document.body.appendChild(capa);
  const $ = (a) => capa.querySelector(`[data-a="${a}"]`);
  let local = "";
  $("cerrar").onclick = () => {
    capa.remove();
    if (local) URL.revokeObjectURL(local);
  };
  const mensaje = (t) => {
    $("cuerpo").innerHTML = `<p style="margin:auto;padding:24px;text-align:center;color:#4b5563;font-size:14px">${t}</p>`;
  };
  try {
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) throw new Error(String(r.status));
    const blob = await r.blob();
    const cd = r.headers.get("content-disposition") || "";
    const delServidor = (cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i) || [])[1];
    let deLaRuta = "";
    try {
      if (!url.startsWith("blob:") && !url.startsWith("data:")) deLaRuta = decodeURIComponent(new URL(url, window.location.href).pathname.split("/").pop() || "");
    } catch (_e) { /* nombre ilegible: se usa el genérico */ }
    const tipo = blob.type || "application/octet-stream";
    const ext = { "application/pdf": "pdf", "image/png": "png", "image/jpeg": "jpg", "text/csv": "csv", "text/plain": "txt",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx" }[tipo.split(";")[0]];
    nombre = nombre || (delServidor ? decodeURIComponent(delServidor) : "") || (deLaRuta.includes(".") ? deLaRuta : "") || `archivo${ext ? "." + ext : ""}`;
    const archivo = new File([blob], nombre, { type: tipo });
    local = URL.createObjectURL(blob);
    $("nombre").textContent = nombre;
    if (tipo.startsWith("image/")) {
      $("cuerpo").innerHTML = `<img alt="" src="${local}" style="max-width:100%;margin:auto">`;
    } else if (tipo.startsWith("application/pdf") || tipo.startsWith("text/")) {
      $("cuerpo").innerHTML = `<iframe title="${nombre.replace(/"/g, "")}" src="${local}" style="flex:1;width:100%;border:0"></iframe>`;
    } else {
      mensaje("Tocá «Compartir / Guardar» para abrirlo con otra app o guardarlo en Archivos.");
    }
    const compartir = $("compartir");
    compartir.disabled = false;
    compartir.style.opacity = "1";
    compartir.onclick = () => {
      if (navigator.canShare?.({ files: [archivo] })) {
        navigator.share({ files: [archivo], title: nombre }).catch(() => {});
      } else {
        window.location.assign(local);
      }
    };
  } catch (_e) {
    $("nombre").textContent = "";
    mensaje("No se pudo abrir el archivo. Revisá la conexión y probá de nuevo.");
  }
}

function prepararArchivosIOS() {
  const abrirOriginal = window.open.bind(window);
  window.open = (url, ...resto) => {
    const u = String(url || "");
    if (!u || !mismoOrigen(u)) return abrirOriginal(url, ...resto);
    mostrarArchivo(u);
    return { closed: false, close() {}, focus() {} };
  };
  const esDescarga = (a) => {
    const u = a.href || "";
    return mismoOrigen(u) && (a.hasAttribute("download") || u.startsWith("blob:") || (a.target === "_blank" && u.includes("/api/")));
  };
  // descargas armadas por código (<a download> + click)
  const clickOriginal = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function click() {
    if (esDescarga(this)) return void mostrarArchivo(this.href, this.getAttribute("download") || "");
    return clickOriginal.call(this);
  };
  const despacharOriginal = HTMLAnchorElement.prototype.dispatchEvent;
  HTMLAnchorElement.prototype.dispatchEvent = function dispatchEvent(ev) {
    if (ev?.type === "click" && !this.isConnected && esDescarga(this)) {
      mostrarArchivo(this.href, this.getAttribute("download") || "");
      return false;
    }
    return despacharOriginal.call(this, ev);
  };
  // enlaces de la pantalla (ver / descargar)
  document.addEventListener("click", (e) => {
    const a = e.target?.closest?.("a[href]");
    if (a && esDescarga(a)) {
      e.preventDefault();
      mostrarArchivo(a.href, a.getAttribute("download") || "");
    }
  }, true);
}

// ── Arranque ────────────────────────────────────────────────────────────────
export function registrarPWA() {
  if (esIOS()) {
    // En iPhone el teclado no agranda la pantalla al tocar un campo; se sigue pudiendo hacer zoom con los dedos.
    const vp = document.querySelector('meta[name="viewport"]');
    if (vp && !/maximum-scale/.test(vp.content)) vp.content += ", maximum-scale=1";
    if (esInstalada()) prepararArchivosIOS();
  }
  if (!PRODUCCION || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js").catch(() => {});
  });
}
